import { Button, Checkbox, Combobox, Input, Text } from "@ttc3k/trekker";
import { createListCollection } from "@ark-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { DateInput } from "shared/components/atoms/DateInput";
import { WhereaboutsModal } from "shared/components";
import { formatRenewalDate } from "shared/utils";
import { Flex } from "styled-system/jsx";

import {
  fixedRenewalDay,
  formatInvoiceAmount,
  invoiceHref,
  renewalDayOnOrAfter,
  type OpenRenewalInvoiceInfo,
} from "./renewalDateUtils";

const CUSTOM_DATE = "__custom__";
const DAY_MS = 24 * 60 * 60 * 1000;

type EditRenewalDateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  /** The member's current renewal day, if one is on file. */
  currentRenewalDate: Date | null;
  fixedDate?: { month: number; day: number } | null;
  reminderLeadDays: number;
  openInvoice?: OpenRenewalInvoiceInfo | null;
  canOfferStatusSync: boolean;
  saving: boolean;
  onSave: (args: {
    date: Date;
    note: string;
    syncStatusTags: boolean;
  }) => void | Promise<void>;
  onRemove: () => void | Promise<void>;
};

/**
 * The rare-case correction tool for a member's renewal date, in either
 * direction. Fixed-date orgs pick from their renewal days (or a custom date);
 * anniversary orgs pick any date.
 */
export const EditRenewalDateModal = ({
  isOpen,
  onClose,
  organizationId,
  currentRenewalDate,
  fixedDate,
  reminderLeadDays,
  openInvoice,
  canOfferStatusSync,
  saving,
  onSave,
  onRemove,
}: EditRenewalDateModalProps) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string>("");
  const [customDate, setCustomDate] = useState<string | undefined>();
  const [note, setNote] = useState("");
  const [syncStatusTags, setSyncStatusTags] = useState(true);

  const occurrences = useMemo(() => {
    if (!fixedDate) return [];
    const thisYear = new Date().getUTCFullYear();
    return Array.from({ length: 9 }, (_, i) =>
      fixedRenewalDay(thisYear - 4 + i, fixedDate.month, fixedDate.day),
    );
  }, [fixedDate]);

  useEffect(() => {
    if (!isOpen) return;
    setNote("");
    setSyncStatusTags(true);
    const current = currentRenewalDate
      ? fixedDate
        ? renewalDayOnOrAfter(currentRenewalDate, fixedDate.month, fixedDate.day)
        : currentRenewalDate
      : null;
    const match = current
      ? occurrences.find(d => d.getTime() === current.getTime())
      : undefined;
    if (fixedDate && match) {
      setSelected(match.toISOString());
      setCustomDate(undefined);
    } else if (current) {
      setSelected(CUSTOM_DATE);
      setCustomDate(current.toISOString().slice(0, 10));
    } else {
      setSelected("");
      setCustomDate(undefined);
    }
  }, [isOpen, currentRenewalDate, fixedDate, occurrences]);

  const todayUtc = useMemo(() => {
    const now = new Date();
    return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  }, []);

  const options = useMemo(
    () => [
      ...occurrences.map(d => ({
        value: d.toISOString(),
        label:
          d.getTime() < todayUtc
            ? t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.FIXED_DATE_PAST_OPTION", {
                date: formatRenewalDate(d, "MMMM d, yyyy"),
              })
            : formatRenewalDate(d, "MMMM d, yyyy"),
      })),
      {
        value: CUSTOM_DATE,
        label: t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.EDIT_CUSTOM_DATE"),
      },
    ],
    [occurrences, t, todayUtc],
  );
  const collection = useMemo(
    () => createListCollection({ items: options }),
    [options],
  );

  const useCustom = !fixedDate || selected === CUSTOM_DATE;
  const chosen = useCustom
    ? customDate
      ? new Date(`${customDate}T00:00:00.000Z`)
      : null
    : selected
      ? new Date(selected)
      : null;
  const validChosen = chosen && !Number.isNaN(chosen.getTime()) ? chosen : null;

  const effect = (() => {
    if (!validChosen) return null;
    const date = formatRenewalDate(validChosen, "MMMM d, yyyy");
    if (validChosen.getTime() <= todayUtc) {
      return t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.EDIT_EFFECT_EXPIRED", { date });
    }
    if (validChosen.getTime() - todayUtc <= reminderLeadDays * DAY_MS) {
      return t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.EDIT_EFFECT_WINDOW", { date });
    }
    return t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.EDIT_EFFECT_PAID_UP", { date });
  })();
  const offerStatusSync =
    canOfferStatusSync &&
    !!validChosen &&
    validChosen.getTime() - todayUtc > reminderLeadDays * DAY_MS;

  const invoiceLink = openInvoice ? invoiceHref(organizationId, openInvoice) : null;

  return (
    <WhereaboutsModal
      isOpen={isOpen}
      onClose={onClose}
      title={t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.EDIT_TITLE")}
      size="md"
      isMaxZIndex
      isOverflowVisible
      saveButtonCopy={t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.EDIT_SAVE")}
      saveButtonProps={{
        disabled: !validChosen || saving,
        isLoading: saving,
        onClick: () => {
          if (!validChosen) return;
          void onSave({
            date: validChosen,
            note,
            syncStatusTags: offerStatusSync && syncStatusTags,
          });
        },
      }}
      cancelButtonProps={{ onClick: onClose }}
    >
      <Flex flexDir="column" gap="300">
        {fixedDate && (
          <Combobox.Root
            collection={collection}
            value={selected ? [selected] : []}
            onValueChange={({ value }: { value: string[] }) =>
              setSelected(value[0] ?? "")
            }
            placeholder={t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.EDIT_PLACEHOLDER")}
            disabled={saving}
            label={t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.EDIT_RENEWS_ON")}
          >
            {options.map(item => (
              <Combobox.Item key={item.value} item={item} {...item} />
            ))}
          </Combobox.Root>
        )}
        {useCustom && (
          <DateInput
            value={customDate}
            onValueChange={setCustomDate}
            title={t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.EDIT_RENEWS_ON")}
            canSelectPastDates
            containerProps={{ maxW: "240px" }}
            triggerProps={{ disabled: saving }}
          />
        )}
        {effect && (
          <Text visual="smallRegular" color="text.mid">
            {effect}
          </Text>
        )}
        {offerStatusSync && (
          <Checkbox
            label={t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.MARK_RENEWED_ALSO_TAG_ACTIVE")}
            checked={syncStatusTags}
            onCheckedChange={({ checked }) => setSyncStatusTags(checked === true)}
          />
        )}
        {openInvoice && (
          <Flex flexDir="column" gap="50" p="200" borderRadius="100" bg="bg.warning.light">
            <Text visual="smallRegular" color="text.dark">
              {t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.EDIT_OPEN_INVOICE_WARNING", {
                amount: formatInvoiceAmount(openInvoice),
                date: openInvoice.dueDate
                  ? formatRenewalDate(openInvoice.dueDate, "MMM d, yyyy")
                  : "",
              })}
            </Text>
            {invoiceLink && (
              <Link to={invoiceLink}>
                <Text visual="smallRegular" color="text.accent.blue.mid">
                  {t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.OPEN_INVOICE")}
                </Text>
              </Link>
            )}
          </Flex>
        )}
        <Input
          label={t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.EDIT_NOTE_LABEL")}
          placeholder={t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.EDIT_NOTE_PLACEHOLDER")}
          value={note}
          onChange={e => setNote(e.target.value)}
          disabled={saving}
        />
        {currentRenewalDate && (
          <Button
            type="button"
            visual="unstyled"
            size="sm"
            alignSelf="flex-start"
            color="text.error"
            textDecoration="underline"
            disabled={saving}
            onClick={() => void onRemove()}
          >
            {t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.EDIT_REMOVE")}
          </Button>
        )}
      </Flex>
    </WhereaboutsModal>
  );
};
